import {createElement} from "react";
import {Navigate, RouteObject, UIMatch, useMatches, useParams} from "react-router";

import {Permission} from "./authSchema";
import {Auth, useAuth} from "./context/AuthProvider";
import {FormattedMessage, MessageDescriptor} from "react-intl";
import {Alert, AlertTitle} from "@mui/material";
import {PageSidebar} from "./components/PageSidebar";

export function convertParams(params: {[s: string]: string}): {[s: string]: any} {
    const res: {[s: string]: any} = {};
    for (const [k, v] of Object.entries(params)) {
        if (k.toLowerCase().endsWith("id")) res[k] = parseInt(v);
        else res[k] = v;
    }
    return res;
}

type CompProps = {[s: string]: any};

/// a react hook that gets auth and returns a boolean
/// it can use other hooks to decide if the page should be hidden
export type HideHook = (auth: Auth) => boolean;

/** A page descriptor.
 *
 * The first generic parameter defines the type of the path of the page itself,
 * The second restricts the type of the path of the items (subpages).
 * It is used to sync the path used in frontend and the code generation that writes a python file
 * with all the paths used in the frontend.
 * */
export type Page<SelfPath = string, ItemPath = string> = {
    /// the path of this page - relative to parent - use :varName for dynamic pages
    readonly path: SelfPath;
    /// translation id to use as label - label is used in the sidebar and the breadcrumbs
    readonly label: MessageDescriptor;
    /// breadcrumb components can use the useParams() from react-router
    /// when not set the label will be used
    readonly breadcrumbComponent?: React.ComponentType<Record<string, never>>;
    /// these components get all the parameters from the path - see useParams()
    readonly contentComponent?: React.ComponentType<CompProps>;
    /// the subpages
    readonly items?: Page<ItemPath>[];
    /// the permission necessary to use this page (and subpages)
    readonly permission?: Permission;

    /// if set, then this hook will be called to check if this page should be hidden (in the sidebar)
    readonly hideHook?: HideHook;
};

function collectSubpages(page: Page): RouteObject[] {
    if (!page.items) return [];

    const routes: RouteObject[] = [];
    for (const subPage of page.items) {
        const s = createRoutes(subPage);
        if (s)
            routes.push({
                path: subPage.path,
                handle: {
                    crumb: () =>
                        subPage.breadcrumbComponent ? (
                            createElement(subPage.breadcrumbComponent)
                        ) : (
                            <FormattedMessage {...subPage.label} />
                        ),
                    sidebar:
                        subPage.items && subPage.items.length > 1
                            ? (basepath: string) => <PageSidebar page={subPage} basePath={basepath} />
                            : null,
                    permission: subPage.permission,
                },
                children: s,
            });
    }
    return routes;
}

type PageHandle = {
    permission?: Permission;
};

// TODO this could be converted into two hooks:
// - one for checking permissions - but then every component would need to call it
//          OTOH checking permission is not mandatory because this is only needed when the user enters an URL by hand
//          (or accesses it from history with a different user), and the backend will check for proper permissions
// - one for converting params to numbers - we could have a useNumbericParams that converts Id params to numbers (like
//    convertParams does), then the component could use that hook instead of relying on getting it in props
function CompatRouteElement({component, page}: {component: React.ComponentType<CompProps>; page: Page}) {
    const params = useParams();
    const auth = useAuth();
    const matches = useMatches() as UIMatch<null, PageHandle>[];

    const neededPermissions: Permission[] = matches
        .map((match) => match.handle?.permission)
        .filter((perm) => Boolean(perm));
    if (page.permission) neededPermissions.push(page.permission);
    if (neededPermissions.find((permission) => !auth.hasPermission(permission))) {
        return (
            <Alert severity="warning" variant="outlined">
                <AlertTitle>
                    <FormattedMessage id="accessDenied.title" defaultMessage="Access denied" />
                </AlertTitle>
                <FormattedMessage
                    id="accessDenied.message"
                    defaultMessage="You don’t have permission to view this page."
                />
            </Alert>
        );
    }
    return createElement(component, convertParams(params));
}

/// creates the routes for the page, the containg path is assumed to be created by the caller
export function createRoutes(page: Page): RouteObject[] {
    const children: RouteObject[] = [];
    children.push({
        index: true,
        element: page.contentComponent ? (
            <CompatRouteElement page={page} component={page.contentComponent} />
        ) : (
            <Navigate to={page.items[0].path} />
        ),
    });

    return children.concat(collectSubpages(page));
}
